import WizardPagetemplate from '../../../../../templates/WizardPagetemplate';
import CoverableDetailsTemplate from '../../../../../templates/CoverableDetailsTemplate';
import { useState } from 'react';
import HouseRentalQuestion from './HouseRentalQuestion';
import HouseForeclosureQuestion from './HouseForeclosureQuestion';
import { allCoverablesOnsaAtom } from '../../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useRecoilValue } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useSaveCoverableInformation } from '../../../../../hooks/useSaveCoverableInformation';
import { COVERABLE_CODE } from '../../../../../constants/coverable';
import type { PrhHouseOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import {
    areHouseUWIQuestionsValid,
    shouldRenderDoorsAge,
    shouldRenderRoofAge20,
    shouldRenderRoofAge30
} from './HouseUWIQuestionsUtil';
import { track } from '../../../../../utils/trackingUtil';
import HouseYesNoFormOnsa from './HouseYesNoFromOnsa';
import { useHippoText } from '../../../../../hooks/useHippoText';

const HouseUWIQuestionsStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const houseCoverables =
        currentProduct.flavourCode === COVERABLE_CODE.prhHouse
            ? coverables.houses ?? []
            : coverables.holidayHomes ?? [];
    const coverableIndex = houseCoverables?.findIndex(
        (coverable) => coverable.fixedId === currentProduct.fixedId
    );
    const currentHouseCoverable = houseCoverables[coverableIndex];

    const [isLoading, setIsLoading] = useState(false);

    const onNext = (): void => {
        setIsLoading(true);
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [currentProduct.flavourCode === COVERABLE_CODE.prhHouse ? 'house' : 'holidayHome']: {
                publicId: currentHouseCoverable.publicId,
                fixedId: currentHouseCoverable.fixedId,
                rental:
                    currentHouseCoverable.flavourCode === 'top_prh_house'
                        ? (currentHouseCoverable as PrhHouseOnsaDTO).rental === 'Yes'
                        : undefined,
                allDoorsAndWindowsOver30AndMadeOfWood:
                    currentHouseCoverable.allDoorsAndWindowsOver30AndMadeOfWood === 'Yes' ||
                    currentHouseCoverable.allDoorsAndWindowsOver30AndMadeOfWood === 'None',
                roofOver30:
                    currentHouseCoverable.roofOver30 === 'Yes' ||
                    currentHouseCoverable.roofOver30 === 'None',
                roofOver20:
                    currentHouseCoverable.roofOver20 === 'Yes' ||
                    currentHouseCoverable.roofOver20 === 'None',
                foreclosureAuction: currentHouseCoverable.foreclosureAuction,
                previousOwnerDeceased: currentHouseCoverable.previousOwnerDeceased
            },
            productToUpdate: currentProduct.flavourName
        };
        const trackCoverable =
            updatedCoverable[
                currentProduct.flavourCode === COVERABLE_CODE.prhHouse ? 'house' : 'holidayHome'
            ];

        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${currentProduct.flavourName}CoverableInformationUWIQuestions`, [
                trackCoverable ?? {}
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            disableNext={
                isLoading ||
                !areHouseUWIQuestionsValid(currentHouseCoverable) ||
                (currentHouseCoverable.flavourCode === COVERABLE_CODE.prhHouse &&
                    (currentHouseCoverable as PrhHouseOnsaDTO).rental === 'Yes')
            }
            onPrevious={goPrevious}
        >
            <CoverableDetailsTemplate>
                <>
                    {currentHouseCoverable.flavourCode === COVERABLE_CODE.prhHouse && (
                        <HouseRentalQuestion />
                    )}
                    {shouldRenderDoorsAge(currentHouseCoverable) && (
                        <HouseYesNoFormOnsa
                            propertyToUpdate="allDoorsAndWindowsOver30AndMadeOfWood"
                            formLabel={getHippoText('DSS-ADDITIONAL-INFORMATION-ALLDOORS-TITLE')}
                            informationModalDescription={getHippoText(
                                'DSS-ADDITIONAL-INFORMATION-INFOLINK-ALLDOORS'
                            )}
                        />
                    )}
                    {shouldRenderRoofAge30(currentHouseCoverable) && (
                        <HouseYesNoFormOnsa
                            propertyToUpdate="roofOver30"
                            formLabel={getHippoText('DSS-ADDITIONAL-INFORMATION-ROOF30-TITLE')}
                            informationModalDescription={getHippoText(
                                'DSS-ADDITIONAL-INFORMATION-INFOLINK-ROOF30'
                            )}
                        />
                    )}
                    {shouldRenderRoofAge20(currentHouseCoverable) && (
                        <HouseYesNoFormOnsa
                            propertyToUpdate="roofOver20"
                            formLabel={getHippoText('DSS-ADDITIONAL-INFORMATION-ROOF20-TITLE')}
                            informationModalDescription={getHippoText(
                                'DSS-ADDITIONAL-INFORMATION-INFOLINK-ROOF20'
                            )}
                        />
                    )}
                    {currentHouseCoverable.buyingHouse === 'Ja' && <HouseForeclosureQuestion />}
                </>
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default HouseUWIQuestionsStep;
